import { Box, Typography } from '@mui/material';
import { format } from 'date-fns';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shift, Worker } from '../../apiTypes';
import Image from '../../components/Image';
import PrimaryButton from '../../components/PrimaryButton';
import { StyledPaper } from '../../constants/styles';
import { t } from '../../constants/translations';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';

const Shifts = () => {
  const { lang } = useLocale();
  const { shifts, workersMap } = useCafe();
  const navitate = useNavigate();

  const navigateToJournal = useCallback(() => {
    navitate('./journal');
  }, [navitate]);

  const shiftsByRole = useMemo(
    () =>
      shifts.reduce<Record<string, (DbDocument<Shift> & { worker: DbDocument<Worker> })[]>>((acc, shift) => {
        const worker = workersMap[shift.workerId];
        if (!worker) {
          return acc;
        }

        return {
          ...acc,
          [worker.role]: [...(acc[worker.role] || []), ...[{ ...shift, worker }]],
        };
      }, {}),
    [shifts, workersMap]
  );

  return (
    <StyledPaper isCard sx={{ p: 2, minWidth: 200, width: 'auto' }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {t.today_shifts[lang]}
      </Typography>
      <Box>
        {shifts.length === 0 && (
          <Typography color={(t) => t.palette.text.secondary} sx={{ textAlign: 'center', mb: 2 }}>
            {t.there_is_nothing[lang]}
          </Typography>
        )}
        {Object.entries(shiftsByRole).map(([role, shifts]) => (
          <Box key={role} sx={{ mb: 2 }}>
            <Typography
              sx={{ ml: 1, whiteSpace: 'nowrap' }}
              variant="subtitle1"
              color={(t) => t.palette.text.secondary}
            >
              {t[role as keyof typeof t][lang]}
            </Typography>

            {shifts.map((shift) => (
              <Box key={shift.id} sx={{ display: 'flex', gap: 1, my: 1 }}>
                <Image src={shift.worker.photoUrl} rounded />
                <Box>
                  <Typography>{shift.worker.name}</Typography>
                  <Typography variant="body2" color={(t) => t.palette.text.secondary}>{`${format(
                    shift.start,
                    'HH:mm'
                  )} - ${format(shift.end, 'HH:mm')}`}</Typography>
                </Box>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      <Box textAlign="right">
        <PrimaryButton variant="text" size="small" color="secondary" onClick={navigateToJournal}>
          {t.journal[lang]}
        </PrimaryButton>
      </Box>
    </StyledPaper>
  );
};

export default Shifts;
